import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Badge, Button, Card, DateInput, formatSum, Select } from '@/components/ui';

/**
 * Сверка платежей: список с фильтрами и итог по проведённым.
 * По каждому платежу доступен журнал обращений провайдера — при споре
 * с Click или Payme это единственное доказательство, что мы ответили верно.
 */

const PROVIDERS: Record<string, string> = { CLICK: 'Click', PAYME: 'Payme', CASH: 'Наличные' };

const STATUSES: Record<string, { label: string; tone: 'neutral' | 'green' | 'red' | 'amber' }> = {
  PENDING: { label: 'Ожидает', tone: 'amber' },
  PAID: { label: 'Оплачен', tone: 'green' },
  CANCELLED: { label: 'Отменён', tone: 'red' },
  REFUNDED: { label: 'Возвращён', tone: 'red' },
  FAILED: { label: 'Не прошёл', tone: 'red' },
};

const PURPOSES: Record<string, string> = {
  RENT: 'Аренда',
  DEPOSIT: 'Залог',
  FINE: 'Штраф',
  LATE_FEE: 'Пеня',
};

interface PaymentRow {
  id: string;
  provider: string;
  purpose: string;
  amount: number;
  status: string;
  providerTransactionId: string | null;
  paidAt: string | null;
  createdAt: string;
  order: { id: string; number: string; status: string };
}

interface PaymentEvent {
  id: string;
  method: string;
  errorCode: number | null;
  payload: unknown;
  response: unknown;
  createdAt: string;
}

export default function PaymentsPage() {
  const [provider, setProvider] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const payments = useQuery({
    queryKey: ['admin-payments', provider, status, from, to],
    queryFn: async () =>
      (
        await api.get<{ items: PaymentRow[]; total: number; paidTotal: number }>(
          '/admin/payments',
          {
            params: {
              provider: provider || undefined,
              status: status || undefined,
              from: from || undefined,
              to: to || undefined,
            },
          },
        )
      ).data,
  });

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold">Платежи</h1>
        <p className="mt-1 text-sm text-neutral-500">
          Сверка с провайдерами: итог считается только по проведённым платежам
        </p>
      </header>

      <Card>
        <div className="mb-3 flex flex-wrap gap-2">
          <Select value={provider} onChange={(e) => setProvider(e.target.value)} className="w-40">
            <option value="">Все провайдеры</option>
            {Object.entries(PROVIDERS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>

          <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-40">
            <option value="">Все статусы</option>
            {Object.entries(STATUSES).map(([value, meta]) => (
              <option key={value} value={value}>
                {meta.label}
              </option>
            ))}
          </Select>

          <DateInput value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" />
          <DateInput value={to} onChange={(e) => setTo(e.target.value)} className="w-40" />
        </div>

        <div className="mb-3 flex items-baseline gap-4 text-sm">
          <span className="text-neutral-500">Найдено: {payments.data?.total ?? 0}</span>
          <span className="font-medium">
            Проведено на {formatSum(payments.data?.paidTotal ?? 0)}
          </span>
        </div>

        {payments.isLoading ? (
          <p className="text-sm text-neutral-500">Загрузка…</p>
        ) : (payments.data?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-neutral-500">Платежей не найдено</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-neutral-500">
                <tr>
                  <th className="py-2">Дата</th>
                  <th>Заказ</th>
                  <th>Провайдер</th>
                  <th>Назначение</th>
                  <th>Статус</th>
                  <th className="text-right">Сумма</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {payments.data!.items.map((payment) => (
                  <tr
                    key={payment.id}
                    className="border-t border-neutral-100 dark:border-neutral-800"
                  >
                    <td className="py-2 text-xs">
                      {new Date(payment.createdAt).toLocaleString('ru-RU')}
                    </td>
                    <td className="font-medium">{payment.order.number}</td>
                    <td>
                      {PROVIDERS[payment.provider] ?? payment.provider}
                      {payment.providerTransactionId && (
                        <span className="block text-xs text-neutral-500">
                          {payment.providerTransactionId}
                        </span>
                      )}
                    </td>
                    <td className="text-xs">{PURPOSES[payment.purpose] ?? payment.purpose}</td>
                    <td>
                      <Badge tone={STATUSES[payment.status]?.tone ?? 'neutral'}>
                        {STATUSES[payment.status]?.label ?? payment.status}
                      </Badge>
                    </td>
                    <td className="text-right font-medium">{formatSum(payment.amount)}</td>
                    <td className="text-right">
                      <Button
                        variant="ghost"
                        onClick={() => setOpenId(openId === payment.id ? null : payment.id)}
                      >
                        Журнал
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {openId && <EventsCard paymentId={openId} />}
    </div>
  );
}

function EventsCard({ paymentId }: { paymentId: string }) {
  const events = useQuery({
    queryKey: ['payment-events', paymentId],
    queryFn: async () =>
      (await api.get<PaymentEvent[]>(`/admin/payments/${paymentId}/events`)).data,
  });

  return (
    <Card title="Журнал обращений провайдера">
      {events.isLoading ? (
        <p className="text-sm text-neutral-500">Загрузка…</p>
      ) : (events.data?.length ?? 0) === 0 ? (
        <p className="text-sm text-neutral-500">Обращений пока нет</p>
      ) : (
        <ul className="space-y-2">
          {events.data!.map((event) => (
            <li
              key={event.id}
              className="rounded-lg border border-neutral-200 p-2 text-xs dark:border-neutral-800"
            >
              <div className="flex items-center gap-2">
                <span className="font-medium">{event.method}</span>
                <span className="text-neutral-500">
                  {new Date(event.createdAt).toLocaleString('ru-RU')}
                </span>
                {event.errorCode !== null && <Badge tone="red">код {event.errorCode}</Badge>}
              </div>
              <pre className="mt-1 overflow-x-auto text-[11px] text-neutral-500">
                {JSON.stringify({ payload: event.payload, response: event.response }, null, 1)}
              </pre>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
